/**
 * GarageIQ — Guide Data Library
 *
 * This file contains all maintenance guide data.
 * In a future version, this will be fetched from a database populated by
 * licensed content from Haynes Publishing (primary) and Chilton/Cengage (secondary).
 *
 * Haynes provides professionally authored repair guides for current vehicles.
 * Chilton covers back-catalog vehicles (pre-2018). The AI processing pipeline
 * will parse this content and populate this structure.
 *
 * Data structure per guide:
 *  - id: unique slug used in the URL
 *  - title: short maintenance task name
 *  - vehicle: year/make/model/trim/engine info
 *  - difficulty: 'beginner' | 'intermediate' | 'advanced'
 *  - duration: estimated time as a string
 *  - description: 1-2 sentence overview
 *  - tools: list of tools needed
 *  - steps: array of step objects (see below)
 *  - parts: array of parts/fluids needed
 *  - tips: pro tips shown at the end
 *  - manualSource: Haynes/Chilton manual reference for this guide
 */

import { tjGuidesAll } from './guideDataTJ.js';
import { bullnoseGuidesAll } from './guideDataBullnose.js';

// ── All guides must be created using Automotive Repair Guide Generation System v10.2 ──────────
// Guidelines:
//   1. OEM fact extraction from verified sources (lemon-manuals, official repair bulletins)
//   2. Independent internet research and synthesis
//   3. Original guide content with proper source attribution (no OEM licensing violations)
//   4. Vehicle structure must use generations array: { make, model, generations: [{ startYear, endYear }], engine?, transmission? }
//   5. V10.2 — embedded YouTube video per guide (see Section 25/26 of methodology PDF)
//

// ── Export all v10.2-compliant guides ──────────────────────────────────────────────────────
export const guides = [
  ...tjGuidesAll,
  ...bullnoseGuidesAll,
];

/**
 * Get a guide by its ID
 * @param {string} id - The guide ID slug
 * @returns {object|null} The guide object, or null if not found
 */
export function getGuideById(id) {
  const guide = guides.find((g) => g.id === id);
  if (!guide) return null;

  // If this is a variant guide, merge with the base guide data
  if (guide._variantOf) {
    const baseGuide = guides.find((g) => g.id === guide._variantOf);
    if (baseGuide) {
      try {
        // Merge: variant metadata overrides base, but inherit steps/parts/tools/etc from base
        const merged = {
          // Start with base guide (has all the detailed content)
          ...baseGuide,
          // Override with variant metadata
          id: guide.id,
          title: guide.title || baseGuide.title,
          category: guide.category || baseGuide.category,
          engineType: guide.engineType || baseGuide.engineType,
          diagramFolder: guide.diagramFolder || baseGuide.diagramFolder,
          // Merge vehicle info: base vehicle + variant overrides
          vehicle: {
            ...baseGuide.vehicle,
            ...guide.vehicle,
          },
        };
        return merged;
      } catch (error) {
        console.error('Error merging guide variant:', error);
        return baseGuide; // Fallback to base guide
      }
    }
  }

  return guide;
}

/**
 * Get all guides for a specific vehicle
 * @param {number} year
 * @param {string} make
 * @param {string} model
 * @returns {object[]} Array of matching guides
 */
export function getGuidesByVehicle(year, make, model) {
  return guides.filter(
    (g) => {
      // Check make and model
      const makesMatch = g.vehicle.make.toLowerCase() === make.toLowerCase();
      const primaryModelMatch = g.vehicle.model.toLowerCase() === model.toLowerCase();
      // Support compatibleModels for cross-model guides (e.g. Bullnose F-150/F-250/F-350)
      const compatibleModelMatch = Array.isArray(g.vehicle.compatibleModels) &&
        g.vehicle.compatibleModels.some(m => m.toLowerCase() === model.toLowerCase());
      const modelsMatch = primaryModelMatch || compatibleModelMatch;

      if (!makesMatch || !modelsMatch) return false;

      // Check year - support both old 'year' property and new 'generations' array
      if (g.vehicle.year !== undefined) {
        // Old format: direct year property
        return g.vehicle.year === year;
      } else if (g.vehicle.generations) {
        // New format: year ranges in generations array
        return g.vehicle.generations.some(
          (gen) => year >= gen.startYear && year <= gen.endYear
        );
      }

      return false;
    }
  );
}

/**
 * Get guides filtered by difficulty
 * @param {'beginner'|'intermediate'|'advanced'} difficulty
 * @returns {object[]}
 */
export function getGuidesByDifficulty(difficulty) {
  return guides.filter((g) => g.difficulty === difficulty);
}

/**
 * Get total parts cost for a guide
 * @param {object} guide
 * @returns {number}
 */
export function getPartsTotalCost(guide) {
  return guide.parts
    .filter((p) => p.category === 'required')
    .reduce((sum, p) => sum + p.price * p.quantity, 0);
}
